import { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { Helmet } from 'react-helmet-async';
import { useAppTheme } from '../../utils/useAppTheme';
import { calculateGematria, getAvailableCiphers, getCipherCategories, getHebrewCiphers, getHebrewCipherCategories } from '../../utils/calculator';
import { saveToResearch } from '../../utils/researchStorage';
import HebrewKeyboard from '../components/HebrewKeyboard';
import WebNumberMeaning from '../components/WebNumberMeaning';
import {
  getFactorization,
  getDivisorsList,
  getDivisorCount,
  getDivisorsSum,
  getNumberTypeInfo,
  getOrdinalSuffix,
  nthPrime,
  nthComposite,
  nthFibonacci,
  nthTriangular,
  nthSquare,
  nthCube,
  nthTetrahedral,
  nthSquarePyramidal,
  nthStar,
  nthPentagonal
} from '../../utils/numberProperties';

export default function ComparePage({ loadedPhraseA, loadedPhraseB }) {
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const [phraseA, setPhraseA] = useState(loadedPhraseA || '');
  const [phraseB, setPhraseB] = useState(loadedPhraseB || '');
  const [resultsA, setResultsA] = useState([]);
  const [resultsB, setResultsB] = useState([]);
  const [isHebrewMode, setIsHebrewMode] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [showMatches, setShowMatches] = useState(true);
  const [showCipherFilter, setShowCipherFilter] = useState(false);
  const [showHebrewKeyboard, setShowHebrewKeyboard] = useState(false);
  const [activeInput, setActiveInput] = useState('A');
  const [compareMode, setCompareMode] = useState('phrases'); // 'phrases' or 'number'

  // Cipher state - managed locally
  const [selectedCiphers, setSelectedCiphers] = useState({});
  const [allCiphers, setAllCiphers] = useState([]);
  const [categories, setCategories] = useState({});
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Load ciphers when language mode changes
  useEffect(() => { loadCiphers(); }, [isHebrewMode]);

  const loadCiphers = () => {
    if (isHebrewMode) {
      const hebrewCiphersObj = getHebrewCiphers();
      const hebrewCats = getHebrewCipherCategories();
      const ciphersArray = Object.keys(hebrewCiphersObj).map(key => ({
        key, name: hebrewCiphersObj[key].name, nameHebrew: hebrewCiphersObj[key].nameHebrew,
        description: hebrewCiphersObj[key].description, category: hebrewCiphersObj[key].category
      }));
      setAllCiphers(ciphersArray);
      const catsFormatted = {};
      Object.keys(hebrewCats).forEach(cat => {
        catsFormatted[cat] = hebrewCats[cat].map(k => {
          const c = hebrewCiphersObj[k];
          return { key: k, name: c.name, nameHebrew: c.nameHebrew, category: cat };
        });
      });
      setCategories(catsFormatted);
      const initial = {};
      const defaults = ['misparhec', 'mispargadol', 'misparsiduri', 'misparkatan'];
      ciphersArray.forEach(c => { initial[c.name] = defaults.includes(c.key); initial[c.key] = defaults.includes(c.key); });
      setSelectedCiphers(initial);
    } else {
      const ciphersObj = getAvailableCiphers();
      const cats = getCipherCategories();
      const ciphersArray = Object.keys(ciphersObj).map(key => ({ key, ...ciphersObj[key] }));
      setAllCiphers(ciphersArray);
      const catsFormatted = {};
      Object.keys(cats).forEach(cat => {
        catsFormatted[cat] = cats[cat].map(k => {
          const c = ciphersObj[k];
          return { key: k, name: c.name, category: cat };
        });
      });
      setCategories(catsFormatted);
      const defaults = ['English Ordinal', 'Full Reduction', 'Reverse Ordinal', 'Reverse Full Reduction'];
      const initial = {};
      ciphersArray.forEach(c => { initial[c.name] = defaults.includes(c.name); });
      setSelectedCiphers(initial);
    }
  };

  const toggleCipher = (name) => setSelectedCiphers(prev => ({ ...prev, [name]: !prev[name] }));
  const selectAll = () => { const all = {}; allCiphers.forEach(c => { all[c.name] = true; }); setSelectedCiphers(all); };
  const clearAll = () => { const none = {}; allCiphers.forEach(c => { none[c.name] = false; }); setSelectedCiphers(none); };
  const toggleCategory = (cat) => setCollapsedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));

  const filteredCiphers = Object.keys(categories).reduce((acc, cat) => {
    const filtered = categories[cat].filter(c =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) || cat.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) acc[cat] = filtered;
    return acc;
  }, {});

  const selectedCount = Object.values(selectedCiphers).filter(Boolean).length;

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const calculateA = useCallback(debounce(async (text, ciphers, hebrew) => {
    if (text.trim()) { const r = await calculateGematria(text, ciphers, hebrew); setResultsA(r); }
    else { setResultsA([]); }
  }, 300), []);

  const calculateB = useCallback(debounce(async (text, ciphers, hebrew) => {
    if (text.trim()) { const r = await calculateGematria(text, ciphers, hebrew); setResultsB(r); }
    else { setResultsB([]); }
  }, 300), []);

  useEffect(() => { calculateA(phraseA, selectedCiphers, isHebrewMode); }, [phraseA, selectedCiphers, isHebrewMode]);
  useEffect(() => { calculateB(phraseB, selectedCiphers, isHebrewMode); }, [phraseB, selectedCiphers, isHebrewMode]);

  useEffect(() => {
    if (loadedPhraseA) {
      setPhraseA(loadedPhraseA);
      // Auto-detect if it's a number to set compareMode
      if (!isNaN(parseInt(loadedPhraseA, 10))) {
        setCompareMode('number');
      } else {
        setCompareMode('phrases');
      }
    }
    if (loadedPhraseB) setPhraseB(loadedPhraseB);
  }, [loadedPhraseA, loadedPhraseB]);

  const toggleHebrewMode = () => {
    setIsHebrewMode(prev => !prev);
    setPhraseA(''); setPhraseB('');
    setResultsA([]); setResultsB([]);
    setShowHebrewKeyboard(false);
  };

  const handleHebrewKeyPress = (key) => {
    const setter = activeInput === 'A' ? setPhraseA : setPhraseB;
    if (key === 'backspace') { setter(prev => prev.slice(0, -1)); }
    else { setter(prev => prev + key); }
  };

  const getMatches = () => {
    if (compareMode === 'number') {
      return [];
    }
    if (resultsA.length === 0 || resultsB.length === 0) return [];
    const matches = [];
    resultsA.forEach(rA => {
      const rB = resultsB.find(r => r.name === rA.name);
      if (rB && rA.totalValue === rB.totalValue) matches.push({ name: rA.name, value: rA.totalValue });
    });
    return matches;
  };

  const handleSaveBoth = async () => {
    if (!phraseA.trim() || !phraseB.trim()) { setSaveMessage('Please enter both values'); setTimeout(() => setSaveMessage(''), 3000); return; }
    
    if (compareMode === 'number') {
      const nA = parseInt(phraseA, 10);
      const nB = parseInt(phraseB, 10);
      if (isNaN(nA) || isNaN(nB)) { setSaveMessage('Please enter valid positive numbers'); setTimeout(() => setSaveMessage(''), 3000); return; }
      try {
        const dummyResultsA = [{ name: 'Number Comparison', category: 'Properties', totalValue: nA }];
        const dummyResultsB = [{ name: 'Number Comparison', category: 'Properties', totalValue: nB }];
        const noteA = `[COMPARE_NUMBERS]${nB}|||`;
        const noteB = `[COMPARE_NUMBERS]${nA}|||`;
        await saveToResearch(phraseA, {}, dummyResultsA, noteA, ['number', 'compare']);
        await saveToResearch(phraseB, {}, dummyResultsB, noteB, ['number', 'compare']);
        setSaveMessage('Both numbers saved to Research!');
        setTimeout(() => setSaveMessage(''), 3000);
      } catch (error) { setSaveMessage(error.message); setTimeout(() => setSaveMessage(''), 3000); }
    } else {
      if (resultsA.length === 0 || resultsB.length === 0) { setSaveMessage('Please wait for calculations'); setTimeout(() => setSaveMessage(''), 3000); return; }
      try {
        const matches = getMatches();
        const matchInfo = matches.length > 0
          ? `${matches.length} match(es): ${matches.map(m => `${m.name}=${m.value}`).join(', ')}`
          : 'no matches';
        await saveToResearch(phraseA, selectedCiphers, resultsA, `[COMPARE]${phraseB}|||${matchInfo}`, ['compare']);
        await saveToResearch(phraseB, selectedCiphers, resultsB, `[COMPARE]${phraseA}|||${matchInfo}`, ['compare']);
        setSaveMessage('Both phrases saved to Research!');
        setTimeout(() => setSaveMessage(''), 3000);
      } catch (error) { setSaveMessage(error.message); setTimeout(() => setSaveMessage(''), 3000); }
    }
  };

  const matches = getMatches();

  const nA = compareMode === 'number' ? parseInt(phraseA, 10) : null;
  const nB = compareMode === 'number' ? parseInt(phraseB, 10) : null;
  const hasAtLeastOneValidNumber = compareMode === 'number' && ((!isNaN(nA) && nA > 0) || (!isNaN(nB) && nB > 0));

  const getNumberCompareRows = () => {
    if (!hasAtLeastOneValidNumber) return [];

    const getVal = (val, func) => {
      if (isNaN(val) || val <= 0) return '-';
      try {
        return func(val);
      } catch (err) {
        return 'Error';
      }
    };

    return [
      { label: 'Factorization', valA: getVal(nA, getFactorization), valB: getVal(nB, getFactorization) },
      { label: 'Divisors Count', valA: getVal(nA, getDivisorCount), valB: getVal(nB, getDivisorCount) },
      { label: 'Divisors Sum', valA: getVal(nA, getDivisorsSum), valB: getVal(nB, getDivisorsSum) },
      { label: 'Prime # Value', valA: getVal(nA, nthPrime), valB: getVal(nB, nthPrime) },
      { label: 'Composite # Value', valA: getVal(nA, nthComposite), valB: getVal(nB, nthComposite) },
      { label: 'Fibonacci # Value', valA: getVal(nA, nthFibonacci), valB: getVal(nB, nthFibonacci) },
      { label: 'Triangular # Value', valA: getVal(nA, nthTriangular), valB: getVal(nB, nthTriangular) },
      { label: 'Square # Value', valA: getVal(nA, nthSquare), valB: getVal(nB, nthSquare) },
      { label: 'Cube # Value', valA: getVal(nA, nthCube), valB: getVal(nB, nthCube) },
      { label: 'Tetrahedral # Value', valA: getVal(nA, nthTetrahedral), valB: getVal(nB, nthTetrahedral) },
      { label: 'Square Pyramidal # Value', valA: getVal(nA, nthSquarePyramidal), valB: getVal(nB, nthSquarePyramidal) },
      { label: 'Star # Value', valA: getVal(nA, nthStar), valB: getVal(nB, nthStar) },
      { label: 'Pentagonal # Value', valA: getVal(nA, nthPentagonal), valB: getVal(nB, nthPentagonal) }
    ];
  };

  const numberCompareRows = getNumberCompareRows();

  return (
    <>
      <Helmet>
        <title>Compare Phrases - Gematria Calculator</title>
        <meta name="description" content="Compare the gematria values of two phrases side by side. Find matching cipher values between words and phrases." />
      </Helmet>
      <View style={[styles.pageContainer, isMobile && styles.pageContainerMobile]}>
        <Text style={styles.pageTitle}>Compare Phrases</Text>
        <Text style={styles.pageSubtitle}>Enter two phrases to compare their gematria values side by side</Text>

        {/* Top Toggles Row */}
        <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 15, marginBottom: 15, flexWrap: 'wrap' }}>
          {/* Mode Toggle */}
          <View style={[styles.languageRow, { marginBottom: 0, alignSelf: 'auto', minWidth: 250 }]}>
            <TouchableOpacity style={[styles.langBtn, compareMode === 'phrases' && styles.langBtnActive]} onPress={() => setCompareMode('phrases')}>
              <Text style={[styles.langBtnText, compareMode === 'phrases' && styles.langBtnTextActive]}>Words</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.langBtn, compareMode === 'number' && styles.langBtnActive]} onPress={() => setCompareMode('number')}>
              <Text style={[styles.langBtnText, compareMode === 'number' && styles.langBtnTextActive]}>Number</Text>
            </TouchableOpacity>
          </View>

          {/* Language Toggle */}
          {compareMode === 'phrases' && (
            <View style={[styles.languageRow, { marginBottom: 0, alignSelf: 'auto', minWidth: 200 }]}>
              <TouchableOpacity style={[styles.langBtn, !isHebrewMode && styles.langBtnActive]} onPress={() => !isHebrewMode || toggleHebrewMode()}>
                <Text style={[styles.langBtnText, !isHebrewMode && styles.langBtnTextActive]}>English</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.langBtn, isHebrewMode && styles.langBtnActive]} onPress={() => isHebrewMode || toggleHebrewMode()}>
                <Text style={[styles.langBtnText, isHebrewMode && styles.langBtnTextActive]}>עברית</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Cipher Filter Toggle */}
        {compareMode === 'phrases' && (
          <TouchableOpacity style={[styles.cipherToggleBtn, showCipherFilter && styles.cipherToggleBtnActive]} onPress={() => setShowCipherFilter(!showCipherFilter)}>
            <Text style={styles.cipherToggleBtnText}>
              {showCipherFilter ? '▲ Hide Cipher Selection' : `▼ Select Ciphers (${selectedCount} selected)`}
            </Text>
          </TouchableOpacity>
        )}

        {/* Collapsible Cipher Filter Panel */}
        {compareMode === 'phrases' && showCipherFilter && (
          <View style={styles.cipherPanel}>
            <TextInput
              style={styles.cipherSearch}
              placeholder="Search ciphers..."
              placeholderTextColor={colors.textSecondary}
              selectionColor={colors.primary}
              value={searchTerm}
              onChangeText={setSearchTerm}
            />
            <View style={styles.cipherActions}>
              <TouchableOpacity style={styles.cipherActionBtn} onPress={selectAll}>
                <Text style={styles.cipherActionText}>Select All</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.cipherActionBtn} onPress={clearAll}>
                <Text style={styles.cipherActionText}>Clear All</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={{ maxHeight: 300 }}>
              {Object.keys(filteredCiphers).map(cat => (
                <View key={cat}>
                  <TouchableOpacity style={styles.catHeader} onPress={() => toggleCategory(cat)}>
                    <Text style={styles.catHeaderText}>{cat}</Text>
                    <Text style={styles.catArrow}>{collapsedCategories[cat] ? '▶' : '▼'}</Text>
                  </TouchableOpacity>
                  {!collapsedCategories[cat] && filteredCiphers[cat].map(cipher => (
                    <TouchableOpacity key={cipher.name} style={styles.cipherItem} onPress={() => toggleCipher(cipher.name)}>
                      <View style={[styles.checkbox, selectedCiphers[cipher.name] && styles.checkboxChecked]}>
                        {selectedCiphers[cipher.name] && <Text style={styles.checkmark}>✓</Text>}
                      </View>
                      <Text style={styles.cipherName}>{cipher.name}</Text>
                      {cipher.nameHebrew && <Text style={styles.cipherNameHebrew}> {cipher.nameHebrew}</Text>}
                    </TouchableOpacity>
                  ))}
                </View>
              ))}
            </ScrollView>
          </View>
        )}

        {/* Input Section - side by side on desktop */}
        <View style={[styles.inputRow, isMobile && styles.inputRowMobile]}>
          <View style={[styles.inputCard, isMobile && styles.inputCardMobile, compareMode === 'phrases' && activeInput === 'A' && isHebrewMode && styles.inputCardActive]}>
            <Text style={styles.inputLabel}>{compareMode === 'phrases' ? 'Phrase 1' : 'Number 1'}</Text>
            <TextInput
              style={[styles.textInput, compareMode === 'phrases' && isHebrewMode && styles.textInputRTL, compareMode === 'number' && { textAlign: 'center', fontSize: 24, fontWeight: 'bold' }]}
              placeholder={compareMode === 'number' ? "e.g., 33" : (isHebrewMode ? "הזן מילים בעברית..." : "Enter phrase...")}
              placeholderTextColor={colors.textSecondary}
              selectionColor={colors.primary}
              value={phraseA} onChangeText={setPhraseA} onFocus={() => setActiveInput('A')}
              multiline={compareMode === 'phrases'}
              keyboardType={compareMode === 'number' ? 'numeric' : 'default'}
            />
          </View>
          <View style={[styles.inputCard, isMobile && styles.inputCardMobile, compareMode === 'phrases' && activeInput === 'B' && isHebrewMode && styles.inputCardActive]}>
            <Text style={styles.inputLabel}>{compareMode === 'phrases' ? 'Phrase 2' : 'Number 2'}</Text>
            <TextInput
              style={[styles.textInput, compareMode === 'number' && { textAlign: 'center', fontSize: 24, fontWeight: 'bold' }, compareMode !== 'number' && isHebrewMode && styles.textInputRTL]}
              placeholder={compareMode === 'number' ? "e.g., 45" : (isHebrewMode ? "הזן מילים בעברית..." : "Enter second phrase...")}
              placeholderTextColor={colors.textSecondary}
              selectionColor={colors.primary}
              value={phraseB} onChangeText={setPhraseB} onFocus={() => setActiveInput('B')} 
              multiline={compareMode === 'phrases'}
              keyboardType={compareMode === 'number' ? 'numeric' : 'default'}
            />
          </View>
        </View>

        {/* Hebrew Keyboard Toggle */}
        {compareMode === 'phrases' && isHebrewMode && (
          <TouchableOpacity style={styles.kbToggle} onPress={() => setShowHebrewKeyboard(!showHebrewKeyboard)}>
            <Text style={styles.kbToggleText}>{showHebrewKeyboard ? '⌨️ Hide Keyboard' : '⌨️ Show Hebrew Keyboard'}</Text>
          </TouchableOpacity>
        )}

        {/* Hebrew Keyboard */}
        {compareMode === 'phrases' && isHebrewMode && showHebrewKeyboard && (
          <View>
            <Text style={styles.activeInputLabel}>Typing into: Phrase {activeInput === 'A' ? '1' : '2'}</Text>
            <HebrewKeyboard onKeyPress={handleHebrewKeyPress} visible={showHebrewKeyboard} />
          </View>
        )}

        {/* Save Button */}
        <TouchableOpacity style={styles.saveBtn} onPress={handleSaveBoth}>
          <Text style={styles.saveBtnText}>💾 Save Both to Research</Text>
        </TouchableOpacity>
        {saveMessage ? <Text style={styles.saveMsg}>{saveMessage}</Text> : null}

        {/* Matches Toggle */}
        {phraseA.trim() && phraseB.trim() && (
          <TouchableOpacity style={[styles.matchesToggle, showMatches && styles.matchesToggleActive]} onPress={() => setShowMatches(!showMatches)}>
            <Text style={styles.matchesToggleText}>
              {matches.length > 0 ? `🎯 ${matches.length} Matching Value${matches.length !== 1 ? 's' : ''}` : '❌ No Matches'}
              {showMatches ? ' ▲' : ' ▼'}
            </Text>
          </TouchableOpacity>
        )}

        {/* Matches Banner - Collapsible */}
        {phraseA.trim() && phraseB.trim() && showMatches && matches.length > 0 && (
          <View style={styles.matchesBanner}>
            <View style={[styles.matchChips, isMobile && styles.matchChipsMobile]}>
              {matches.map((m, i) => (
                <View key={i} style={styles.matchChip}>
                  <Text style={styles.matchChipName}>{m.name}</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Text style={styles.matchChipValue}>{m.value}</Text>
                    <WebNumberMeaning number={m.value} />
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Results Table - Phrase mode */}
        {compareMode === 'phrases' && resultsA.length > 0 && (
          <View style={styles.tableCard}>
            <Text style={styles.tableTitle}>All Cipher Values</Text>
            <View style={styles.tableHeader}>
              <Text style={[styles.thText, styles.cipherCol]}>Cipher</Text>
              <Text style={[styles.thText, styles.valCol]}>Phrase 1</Text>
              <Text style={[styles.thText, styles.valCol]}>Phrase 2</Text>
              <Text style={[styles.thText, styles.statusCol]}>Match</Text>
            </View>
            {resultsA.map((rA, i) => {
              const rB = resultsB.find(r => r.name === rA.name);
              const isMatch = rB && rA.totalValue === rB.totalValue;
              const valB = rB ? rB.totalValue : '-';

              return (
                <View key={i} style={[styles.tableRow, isMatch && styles.tableRowMatch, i % 2 === 0 && styles.tableRowAlt]}>
                  <Text style={[styles.tdText, styles.cipherCol]} numberOfLines={1}>{rA.name}</Text>
                  
                  <View style={[styles.valCol, { justifyContent: 'center', alignItems: 'center' }]}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <TouchableOpacity onPress={() => window.location.href = `/number?num=${rA.totalValue}`}>
                        <Text style={[styles.tdValue, {textDecorationLine: 'underline', color: colors.primary}]}>{rA.totalValue}</Text>
                      </TouchableOpacity>
                      <WebNumberMeaning number={rA.totalValue} />
                    </View>
                  </View>

                  <View style={[styles.valCol, { justifyContent: 'center', alignItems: 'center' }]}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <TouchableOpacity onPress={() => valB !== '-' ? window.location.href = `/number?num=${valB}` : null}>
                        <Text style={[styles.tdValue, valB !== '-' && {textDecorationLine: 'underline', color: colors.primary}]}>{valB}</Text>
                      </TouchableOpacity>
                      {valB !== '-' && <WebNumberMeaning number={valB} />}
                    </View>
                  </View>

                  <Text style={[styles.tdStatus, styles.statusCol]}>{isMatch ? '✅' : ''}</Text>
                </View>
              );
            })}
          </View>
        )}

        {/* Results Table - Number mode */}
        {compareMode === 'number' && hasAtLeastOneValidNumber && (
          <View style={styles.tableCard}>
            <Text style={styles.tableTitle}>Number Properties Comparison</Text>
            <View style={styles.tableHeader}>
              <Text style={[styles.thText, styles.cipherCol]}>Property</Text>
              {!isNaN(nA) && nA > 0 ? (
                <View style={[styles.valCol, { justifyContent: 'center', alignItems: 'center' }]}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <TouchableOpacity onPress={() => window.location.href = `/number?num=${nA}`}>
                      <Text style={[styles.tdValue, {textDecorationLine: 'underline', color: colors.primary, fontWeight: 'bold'}]}>{nA}</Text>
                    </TouchableOpacity>
                    <WebNumberMeaning number={nA} />
                  </View>
                </View>
              ) : (
                <View style={[styles.valCol, {justifyContent: 'center', alignItems: 'center'}]}>
                  <Text style={[styles.tdValue, {fontWeight: 'bold'}]}>-</Text>
                </View>
              )}
              {!isNaN(nB) && nB > 0 ? (
                <View style={[styles.valCol, { justifyContent: 'center', alignItems: 'center' }]}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <TouchableOpacity onPress={() => window.location.href = `/number?num=${nB}`}>
                      <Text style={[styles.tdValue, {textDecorationLine: 'underline', color: colors.primary, fontWeight: 'bold'}]}>{nB}</Text>
                    </TouchableOpacity>
                    <WebNumberMeaning number={nB} />
                  </View>
                </View>
              ) : (
                <View style={[styles.valCol, {justifyContent: 'center', alignItems: 'center'}]}>
                  <Text style={[styles.tdValue, {fontWeight: 'bold'}]}>-</Text>
                </View>
              )}
              <Text style={[styles.thText, styles.statusCol]}>Match</Text>
            </View>
            {numberCompareRows.map((row, i) => {
              const isMatch = row.valA !== '-' && row.valB !== '-' && row.valA.toString() === row.valB.toString();
              const parseValA = parseInt(row.valA, 10);
              const parseValB = parseInt(row.valB, 10);
              const isValANum = !isNaN(parseValA) && parseValA > 0;
              const isValBNum = !isNaN(parseValB) && parseValB > 0;

              return (
                <View key={i} style={[styles.tableRow, isMatch && styles.tableRowMatch, i % 2 === 0 && styles.tableRowAlt]}>
                  <Text style={[styles.tdText, styles.cipherCol]} numberOfLines={1}>{row.label}</Text>
                  
                  {isValANum ? (
                    <TouchableOpacity onPress={() => window.location.href = `/number?num=${parseValA}`} style={[styles.valCol, {justifyContent: 'center', alignItems: 'center'}]}>
                      <Text style={[styles.tdValue, {textDecorationLine: 'underline', color: colors.primary}]}>{row.valA}</Text>
                    </TouchableOpacity>
                  ) : (
                    <View style={[styles.valCol, {justifyContent: 'center', alignItems: 'center'}]}>
                      <Text style={styles.tdValue}>{row.valA}</Text>
                    </View>
                  )}

                  {isValBNum ? (
                    <TouchableOpacity onPress={() => window.location.href = `/number?num=${parseValB}`} style={[styles.valCol, {justifyContent: 'center', alignItems: 'center'}]}>
                      <Text style={[styles.tdValue, {textDecorationLine: 'underline', color: colors.primary}]}>{row.valB}</Text>
                    </TouchableOpacity>
                  ) : (
                    <View style={[styles.valCol, {justifyContent: 'center', alignItems: 'center'}]}>
                      <Text style={styles.tdValue}>{row.valB}</Text>
                    </View>
                  )}

                  <Text style={[styles.tdStatus, styles.statusCol]}>{isMatch ? '✅' : ''}</Text>
                </View>
              );
            })}
          </View>
        )}

        {((compareMode === 'phrases' && !phraseA.trim() && !phraseB.trim()) || 
          (compareMode === 'number' && !hasAtLeastOneValidNumber)) && (
          <Text style={styles.emptyText}>
            {compareMode === 'phrases' 
              ? 'Enter two phrases above to compare their gematria values'
              : 'Enter two positive numbers above to compare their properties'}
          </Text>
        )}
      </View>
    </>
  );
}

const createStyles = (colors) => StyleSheet.create({
  pageContainer: { flex: 1, padding: 30, maxWidth: 1000, alignSelf: 'center', width: '100%' },
  pageContainerMobile: { padding: 15 },
  pageTitle: { fontSize: 28, fontWeight: 'bold', color: colors.text, textAlign: 'center', marginBottom: 5 },
  pageSubtitle: { fontSize: 15, color: colors.textSecondary, textAlign: 'center', marginBottom: 20 },
  languageRow: { flexDirection: 'row', marginBottom: 15, borderRadius: 8, overflow: 'hidden', borderWidth: 2, borderColor: colors.primary, maxWidth: 400, alignSelf: 'center' },
  languageRowMobile: { maxWidth: '100%' },
  langBtn: { flex: 1, paddingVertical: 12, paddingHorizontal: 20, backgroundColor: colors.mutedSurface, alignItems: 'center', justifyContent: 'center' },
  langBtnActive: { backgroundColor: colors.primary },
  langBtnText: { fontSize: 15, fontWeight: '600', color: colors.textSecondary, textAlign: 'center' },
  langBtnTextActive: { color: colors.onPrimary },
  // Cipher filter toggle button
  cipherToggleBtn: { backgroundColor: colors.primarySurface, paddingVertical: 12, paddingHorizontal: 20, borderRadius: 8, alignItems: 'center', marginBottom: 0, borderWidth: 1, borderColor: colors.primary },
  cipherToggleBtnActive: { borderBottomLeftRadius: 0, borderBottomRightRadius: 0, marginBottom: 0 },
  cipherToggleBtnText: { fontSize: 15, fontWeight: '600', color: colors.primary },
  // Cipher filter panel
  cipherPanel: { backgroundColor: colors.surfaceElevated, borderRadius: 8, borderTopLeftRadius: 0, borderTopRightRadius: 0, padding: 15, marginBottom: 20, borderWidth: 1, borderColor: colors.primary, borderTopWidth: 0, shadowColor: colors.shadow, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 8, elevation: 3 },
  cipherSearch: { color: colors.text, backgroundColor: colors.inputBackground, borderWidth: 1, borderColor: colors.border, borderRadius: 5, padding: 10, fontSize: 14, marginBottom: 10, outlineColor: colors.primary },
  cipherActions: { flexDirection: 'row', gap: 10, marginBottom: 10 },
  cipherActionBtn: { backgroundColor: colors.primary, paddingVertical: 6, paddingHorizontal: 14, borderRadius: 4 },
  cipherActionText: { color: colors.onPrimary, fontSize: 13, fontWeight: '600' },
  catHeader: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8, paddingHorizontal: 5, backgroundColor: colors.primarySurface, borderRadius: 4, marginBottom: 2 },
  catHeaderText: { fontWeight: 'bold', fontSize: 14, color: colors.text },
  catArrow: { fontSize: 12, color: colors.textSecondary },
  cipherItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 10 },
  checkbox: { width: 20, height: 20, borderRadius: 4, borderWidth: 2, borderColor: colors.border, marginRight: 10, alignItems: 'center', justifyContent: 'center' },
  checkboxChecked: { backgroundColor: colors.primary, borderColor: colors.primary },
  checkmark: { color: colors.onPrimary, fontSize: 12, fontWeight: 'bold' },
  cipherName: { fontSize: 14, color: colors.text },
  cipherNameHebrew: { fontSize: 13, color: colors.textSecondary },
  // Input section
  inputRow: { flexDirection: 'row', gap: 20, marginBottom: 15, marginTop: 15 },
  inputRowMobile: { flexDirection: 'column', gap: 15 },
  inputCard: { flex: 1, backgroundColor: colors.surfaceElevated, borderRadius: 8, padding: 15, shadowColor: colors.shadow, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 8, elevation: 3 },
  inputCardMobile: { width: '100%' },
  inputCardActive: {},
  inputLabel: { fontSize: 16, fontWeight: '700', color: colors.text, marginBottom: 10 },
  textInput: { color: colors.text, backgroundColor: colors.inputBackground, borderWidth: 1, borderColor: colors.border, borderRadius: 5, padding: 12, fontSize: 16, minHeight: 80, textAlignVertical: 'top', outlineColor: colors.primary },
  textInputRTL: { textAlign: 'right', writingDirection: 'rtl' },
  // Hebrew keyboard
  kbToggle: { backgroundColor: colors.primarySurface, paddingVertical: 10, paddingHorizontal: 20, borderRadius: 8, alignItems: 'center', marginBottom: 10, borderWidth: 1, borderColor: colors.primary },
  kbToggleText: { fontSize: 15, fontWeight: '600', color: colors.primary },
  activeInputLabel: { textAlign: 'center', fontSize: 14, color: colors.primary, fontWeight: '600', marginBottom: 5 },
  // Save
  saveBtn: { backgroundColor: colors.primary, paddingVertical: 12, paddingHorizontal: 30, borderRadius: 6, alignItems: 'center', alignSelf: 'center', marginBottom: 10 },
  saveBtnText: { color: colors.onPrimary, fontSize: 16, fontWeight: '600' },
  saveMsg: { textAlign: 'center', color: colors.success, fontSize: 14, marginBottom: 15 },
  // Matches
  matchesBanner: { borderRadius: 8, padding: 15, marginBottom: 25, alignItems: 'center', backgroundColor: colors.successSurface, borderWidth: 1, borderColor: colors.success, borderTopLeftRadius: 0, borderTopRightRadius: 0 },
  matchesToggle: { backgroundColor: colors.mutedSurface, paddingVertical: 12, paddingHorizontal: 20, borderRadius: 8, alignItems: 'center', marginBottom: 0, marginTop: 10, borderWidth: 1, borderColor: colors.border },
  matchesToggleActive: { backgroundColor: colors.successSurface, borderColor: colors.success, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, marginBottom: 0 },
  matchesToggleText: { fontSize: 16, fontWeight: '600', color: colors.text },
  matchChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, justifyContent: 'center' },
  matchChipsMobile: { gap: 8 },
  matchChip: { backgroundColor: colors.surfaceElevated, borderRadius: 20, paddingVertical: 8, paddingHorizontal: 16, flexDirection: 'row', alignItems: 'center', gap: 8, shadowColor: colors.shadow, shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 3, elevation: 2 },
  matchChipName: { fontSize: 13, color: colors.textSecondary },
  matchChipValue: { fontSize: 15, fontWeight: 'bold', color: colors.success },
  // Table
  tableCard: { backgroundColor: colors.surfaceElevated, borderRadius: 8, padding: 20, shadowColor: colors.shadow, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 8, elevation: 3 },
  tableTitle: { fontSize: 20, fontWeight: 'bold', color: colors.text, marginBottom: 15 },
  tableHeader: { flexDirection: 'row', paddingVertical: 10, paddingHorizontal: 10, borderBottomWidth: 2, borderBottomColor: colors.primary, backgroundColor: colors.primarySurface, borderRadius: 4 },
  thText: { fontWeight: 'bold', fontSize: 14, color: colors.text },
  cipherCol: { flex: 3 },
  valCol: { flex: 1, textAlign: 'center' },
  statusCol: { width: 50, textAlign: 'center' },
  tableRow: { flexDirection: 'row', paddingVertical: 10, paddingHorizontal: 10, borderBottomWidth: 1, borderBottomColor: colors.lightBorder, alignItems: 'center' },
  tableRowAlt: { backgroundColor: colors.mutedSurface },
  tableRowMatch: { backgroundColor: colors.successSurface },
  tdText: { fontSize: 14, color: colors.text },
  tdValue: { fontSize: 15, fontWeight: '600', color: colors.text },
  tdStatus: { fontSize: 16 },
  emptyText: { color: colors.textSecondary, fontStyle: 'italic', textAlign: 'center', marginTop: 60, fontSize: 16 },
});
